import $ from "jquery";

import render_sign_topic_receipt_modal from "../templates/sign_topic_receipt_modal.hbs";

import * as channel from "./channel.ts";
import * as dialog_widget from "./dialog_widget.ts";
import {$t, $t_html} from "./i18n.ts";
import * as ui_report from "./ui_report.ts";

interface SigningAccountOption {
    user_id: number;
    is_current: boolean;
    is_group_account: boolean;
    is_individual_group: boolean;
    can_sign: boolean;
    requires_representative: boolean;
    is_individual_signer: boolean;
    display_name: string;
}

interface SigningAccountsResponse {
    accounts: SigningAccountOption[];
}

interface TopicSignatureVisibilityResponse {
    can_toggle_signature_page_visibility: boolean;
    signature_page_is_public: boolean;
    topic_id: number;
}

export function show_sign_topic_receipt_modal(
    stream_name: string,
    topic_name: string,
    message_id: number,
): void {
    let selected_signing_user_id: number | undefined;
    let selected_representative_user_id: number | undefined;

    function submit_topic_receipt_signature(action: "sign" | "void"): void {
        const signing_account_select = document.querySelector(
            "#sign-topic-signing-account-select",
        ) as HTMLSelectElement | null;
        if (signing_account_select !== null) {
            selected_signing_user_id = Number.parseInt(signing_account_select.value, 10);
        }
        const representative_account_select = document.querySelector(
            "#sign-topic-representative-account-select",
        ) as HTMLSelectElement | null;
        if (representative_account_select !== null && representative_account_select.value !== "") {
            selected_representative_user_id = Number.parseInt(representative_account_select.value, 10);
        } else {
            selected_representative_user_id = undefined;
        }

        const data: {
            action: "sign" | "void";
            signing_user_id?: number;
            representative_user_id?: number;
        } = {action};
        if (selected_signing_user_id !== undefined) {
            data.signing_user_id = selected_signing_user_id;
        }
        if (selected_representative_user_id !== undefined) {
            data.representative_user_id = selected_representative_user_id;
        }
        void channel.post({
            url: `/json/messages/${encodeURIComponent(message_id)}/topic_receipt_signature`,
            data,
            cache: false,
            success() {
                dialog_widget.close();
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
                dialog_widget.hide_dialog_spinner();
            },
        });
    }

    function submit_topic_receipt_visibility(visibility: "private" | "public"): void {
        void channel.post({
            url: `/json/messages/${encodeURIComponent(message_id)}/topic_receipt_signature/visibility`,
            data: {visibility},
            cache: false,
            success() {
                dialog_widget.close();
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
                dialog_widget.hide_dialog_spinner();
            },
        });
    }

    function render_signing_account_selectors($modal: JQuery, accounts: SigningAccountOption[]): void {
        const signable_accounts = accounts.filter((account) => account.can_sign);
        if (signable_accounts.length === 0) {
            return;
        }

        const individual_accounts = signable_accounts.filter((account) => account.is_individual_signer);
        const default_signing_account =
            signable_accounts.find((account) => account.is_current) ?? signable_accounts[0];
        if (default_signing_account === undefined) {
            return;
        }
        selected_signing_user_id = default_signing_account.user_id;

        const signing_options_html = signable_accounts
            .map((account) => {
                const selected_attr = account.user_id === default_signing_account.user_id ? "selected" : "";
                const escaped_name = $("<div>").text(account.display_name).html();
                const requires_representative = account.requires_representative ? "true" : "false";
                return `<option value="${account.user_id}" data-requires-representative="${requires_representative}" ${selected_attr}>${escaped_name}</option>`;
            })
            .join("");

        const representative_options_html = individual_accounts
            .map((account) => {
                const escaped_name = $("<div>").text(account.display_name).html();
                return `<option value="${account.user_id}">${escaped_name}</option>`;
            })
            .join("");

        const selectors_html = `
            <div class="sign-message-receipt-account-selectors">
                <label class="sign-message-receipt-account-label" for="sign-topic-signing-account-select">
                    ${$t({defaultMessage: "Sign as"})}
                </label>
                <select id="sign-topic-signing-account-select" class="sign-message-receipt-account-select">
                    ${signing_options_html}
                </select>
                <div id="sign-topic-representative-account-container" class="sign-message-receipt-representative-container">
                    <label class="sign-message-receipt-account-label" for="sign-topic-representative-account-select">
                        ${$t({defaultMessage: "Representative"})}
                    </label>
                    <select id="sign-topic-representative-account-select" class="sign-message-receipt-account-select">
                        ${representative_options_html}
                    </select>
                </div>
            </div>
        `;
        const $selectors = $(selectors_html);
        $modal.find(".modal__footer").append($selectors);

        const update_representative_visibility = (): void => {
            const $selected_option = $selectors.find("#sign-topic-signing-account-select option:selected");
            const requires_representative =
                $selected_option.attr("data-requires-representative") === "true";
            const $representative_container = $selectors.find(
                "#sign-topic-representative-account-container",
            );
            if (requires_representative) {
                $representative_container.show();
            } else {
                $representative_container.hide();
                selected_representative_user_id = undefined;
            }
        };

        $selectors.find("#sign-topic-signing-account-select").on("change", function (this: HTMLElement) {
            const value = ($(this).val() as string | undefined) ?? "";
            selected_signing_user_id = Number.parseInt(value, 10);
            update_representative_visibility();
        });
        $selectors
            .find("#sign-topic-representative-account-select")
            .on("change", function (this: HTMLElement) {
                const value = ($(this).val() as string | undefined) ?? "";
                selected_representative_user_id = Number.parseInt(value, 10);
            });

        update_representative_visibility();
    }

    function add_visibility_button(
        $modal: JQuery,
        can_toggle_signature_page_visibility: boolean,
        signature_page_is_public: boolean,
    ): void {
        if (!can_toggle_signature_page_visibility) {
            return;
        }
        const visibility = signature_page_is_public ? "private" : "public";
        const button_label = signature_page_is_public
            ? $t({defaultMessage: "Make private"})
            : $t({defaultMessage: "Make public"});
        const $visibility_button = $(
            `<button type="button" class="modal__button dialog_exit_button sign-message-receipt-visibility-button">${button_label}</button>`,
        );
        $modal.find(".modal__footer .dialog_exit_button").before($visibility_button);
        $visibility_button.on("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            dialog_widget.show_dialog_spinner();
            submit_topic_receipt_visibility(visibility);
        });
    }

    function post_render(): void {
        const $modal = $("#sign_topic_receipt_modal");
        $modal.find(".modal__footer").addClass("sign-message-receipt-footer");
        const $submit_button = $modal.find(".dialog_submit_button");
        const $submit_button_text = $submit_button.find(".submit-button-text");
        const $submit_button_container = $modal.find(".modal__footer .dialog_submit_button_container");
        $modal.find(".dialog_submit_button").addClass("sign-message-receipt-sign-button");

        void channel.get({
            url: `/json/messages/${encodeURIComponent(message_id)}/topic_receipt_signature`,
            cache: false,
            success(data) {
                render_signing_account_selectors($modal, (data as SigningAccountsResponse).accounts);
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
            },
        });

        void channel.get({
            url: `/json/messages/${encodeURIComponent(message_id)}/topic_receipt_signature/visibility`,
            cache: false,
            success(data) {
                const visibility_data = data as TopicSignatureVisibilityResponse;
                add_visibility_button(
                    $modal,
                    visibility_data.can_toggle_signature_page_visibility,
                    visibility_data.signature_page_is_public,
                );

                const $void_button = $(
                    `<button type="button" class="modal__button sign-message-receipt-void-button">${$t({defaultMessage: "VOID"})}</button>`,
                );
                $submit_button_text.text($t({defaultMessage: "SIGN"}));
                $submit_button_container.before($void_button);
                $void_button.on("click", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    dialog_widget.show_dialog_spinner();
                    submit_topic_receipt_signature("void");
                });
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
            },
        });
    }

    dialog_widget.launch({
        html_heading: $t_html({defaultMessage: "Sign topic receipt"}),
        html_body: render_sign_topic_receipt_modal({
            stream_name,
            topic_name,
        }),
        html_submit_button: $t_html({defaultMessage: "SIGN"}),
        html_exit_button: $t_html({defaultMessage: "Cancel"}),
        id: "sign_topic_receipt_modal",
        form_id: "sign_topic_receipt_form",
        on_click: () => {
            submit_topic_receipt_signature("sign");
        },
        post_render,
        loading_spinner: true,
    });
}
